<template>
  <div />
</template>

<script>
import axios from 'axios'
import moment from 'moment'

import Mapbox from 'mapbox-gl'

const M_PER_SECOND_TO_KNOTS_CONVERSION_FACTOR = 1.94384

export default {
  name: 'WindLayer',
  inject: ['mapbox', 'map', 'actions'],
  props: {
    raceTime: {
      type: String,
      default: ''
    },
    forecastTime: { /* number of hours (max 12) to forecast wind, 1 hr increments */
      type: Number,
      default: 0
    },
    isPreRace: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      windData: [],
    }
  },
  computed: {

  },
  watch: {
    raceTime (newVal, oldVal) {
      this.updateWind()
      if (this._reloadWind) clearInterval(this._reloadWind)
      if (newVal === '') {
        this._reloadWind = setInterval(() => {
          this.updateWind()
        }, 10 * 60 * 1000) /* set up minute timer */
      }
    },
    forecastTime (newVal, oldVal) {
      this.updateWind()
      if (this._reloadWind) clearInterval(this._reloadWind)
      if (newVal === '') {
        this._reloadWind = setInterval(() => {
          this.updateWind()
        }, 10 * 60 * 1000) /* set up minute timer */
      }
    }
  },
  async created() {
    await this.loadImage('arrow', '/tracker/TrackerIcons/fwd-arrow.png')
  },
  mounted() {
    this.updateWind()

    if (this.raceTime === '') {
      this._reloadWind = setInterval(() => {
        this.updateWind()
      }, 10 * 60 * 1000) /* set up minute timer */
    }
  },
  beforeDestroy() {
    clearInterval(this._reloadWind)
    if (this.map.getLayer('windSpeed-heat')) {
      this.map.removeLayer('windSpeed-heat')
    }
    if (this.map.getSource('windSpeed')) {
      this.map.removeSource('windSpeed')
    }

    if (this.map.getLayer('wind')) {
      this.map.removeLayer('wind')
    }
    if (this.map.getSource('wind')) {
      this.map.removeSource('wind')
    }

    if (this.map.getLayer('windArrows')) {
      this.map.removeLayer('windArrows')
    }
    if (this.map.getSource('windArrows')) {
      this.map.removeSource('windArrows')
    }
  },
  methods: {
    windTime (raceTime, forecastTime, isPreRace) {
      if ( isPreRace ){
        return '';
      }
      const m = moment(raceTime, 'YYYYMMDDHHmmss')

      /* convert to UTC */
      const windTime = m.subtract(11, 'hours')

      /* add forecast hours */
      windTime.add(forecastTime, 'hours')

      /* convert to string */
      return windTime.format('YYYYMMDDHHmmss')
    },

    updateWind () {
      axios
      .get(`${window.BASE_FEEDS_URL}Weather?raceTime=${this.windTime(this.raceTime, this.forecastTime, this.isPreRace)}`)
      .then(response => {
        this.windData = response.data
        this.drawWind()
      })
      .catch(error => {
        this.errored = true
        // eslint-disable-next-line
          console.log(error)
      })
    },
    loadImage(name, url) {
      const vm = this
      return new Promise((resolve, reject) => {
        vm.map.loadImage(url, (error, image) => {
          if (error) {
            reject()
          }
          if (!vm.map.hasImage(name)) {
            vm.map.addImage(name, image)
          }
          resolve()
        })
      })
    },
    drawWind() {
      const windScale = 35
      const windLines = []
      const windArrows = []
      const windSpeed = []

      for (let i = 0; i < this.windData.length; i++) {
        const wind = this.windData[i]

        const lineCoordinates = [
          [wind.Coordinate.Longitude, wind.Coordinate.Latitude],
          [wind.Coordinate.Longitude + (wind.UWind / windScale), wind.Coordinate.Latitude + (wind.VWind / windScale)]
        ]

        const pointCoordinates = [wind.Coordinate.Longitude + (wind.UWind / windScale), wind.Coordinate.Latitude + (wind.VWind / windScale)]

        windLines.push({
          'type': 'symbol',
          'geometry': {
            'type': 'LineString',
            'coordinates': lineCoordinates
          },
          'properties': {}
        })


        const diffLat = lineCoordinates[1][1] - lineCoordinates[0][1]
        const diffLng = lineCoordinates[1][0] - lineCoordinates[0][0]
        const angle = 360 - (Math.atan2(diffLat, diffLng) * 57.295779513082) /* magic number? */
        const velocity = Math.sqrt(wind.UWind**2 + wind.VWind**2) * M_PER_SECOND_TO_KNOTS_CONVERSION_FACTOR /* knots */

        windArrows.push({
          'type': 'Feature',
          'geometry': {
            'type': 'Point',
            'coordinates': pointCoordinates
          },
          'properties': {
            'direction': angle,
            'speed': velocity
          }
        })

        windSpeed.push({
          'type': 'Feature',
          'geometry': {
            'type': 'Point',
            'coordinates': pointCoordinates
          },
          'properties': {
            'velocity': velocity
          }
        })

      }

      /* heatmap of speed */
      if (this.map.getLayer('windSpeed-heat')) {
        this.map.removeLayer('windSpeed-heat')
      }
      if (this.map.getSource('windSpeed')) {
        this.map.removeSource('windSpeed')
      }
      const speedSource = {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': windSpeed
        }
      }

      this.map.addSource('windSpeed', speedSource)

      this.map.addLayer({
        'id': 'windSpeed-heat',
        'type': 'heatmap',
        'source': 'windSpeed',
        'paint': {
          'heatmap-weight': {
            property: 'velocity',
            stops: [
              [0, 0],
              [60, 1]
            ]
          },
          'heatmap-intensity': {
            stops: [
              [0, 0],
              [3, 0.1],
              [10, 1]
            ],
          },
          // 'heatmap-color': [ // BOM colours (not as nice)
          //   'interpolate',
          //   ['linear'],
          //   ['heatmap-density'],
          //   0, '#EFF8FE', // 0-5 knots
          //   0.1, '#CDF0FF', // 5-10 knots
          //   0.2, '#9CDBFC', // 10-15 knots
          //   0.3, '#ADFFA7', // 15-20 knots
          //   0.4, '#7FDF78', // 20-25 knots
          //   0.5, '#E6E675', // 25-30 knots
          //   0.6, '#FF7E4C', // 30-35 knots
          //   0.7, '#E5270D', // 35-40 knots
          //   0.8, '#9A0000', // 40+ knots
          // ],
          'heatmap-radius': {
            stops: [
              [0, 1],
              [10, 200]
            ]
          },
          'heatmap-opacity': {
            stops: [
              [0, 0.5],
              [10, 0]
            ]
          }
        }
      })

      /* lines */
      if (this.map.getLayer('wind')) {
        this.map.removeLayer('wind')
      }
      if (this.map.getSource('wind')) {
        this.map.removeSource('wind')
      }
      const windSource = {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': windLines
        }
      }
      this.map.addSource('wind', windSource)

      this.map.addLayer({
        'id': 'wind',
        'type': 'line',
        'source': 'wind',
        'layout': {
          'line-cap': 'round'
        },
        'paint': {
          'line-color': '#fff',
          'line-width': 1
        }
      })

      /* arrow heads on lines */
      if (this.map.getLayer('windArrows')) {
        this.map.removeLayer('windArrows')
      }
      if (this.map.getSource('windArrows')) {
        this.map.removeSource('windArrows')
      }
      const arrowSource = {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': windArrows
        }
      }

      this.map.addSource('windArrows', arrowSource)

      this.map.addLayer({
        'id': 'windArrows',
        'type': 'symbol',
        'source': 'windArrows',
        'layout': {
          'icon-image': 'arrow',
          'icon-rotate': ['get', 'direction']
        }
      })
    }
  }
}
</script>